import ComboBox from '@/Components/UI/Shadcn/ComboBox';
import { isBlankOptionVisible } from '@/helpers/spreadsheetHelper';
import { useCurrentFile } from '@/hooks/useCurrentFile';
import { setAutocompleteColumns } from '@/redux/reducers/spreadsheetSlice';
import { get } from '@/Utils/API';
import { AUTOCOMPLETE, QUERY_PARAM_OMIT_EMPTY_VALUES } from '@/Utils/constants';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

const SuggestionBasedFilter = ({
  cypressTextFieldName,
  defaultValue,
  onChange,
  disabled,
  currFieldType,
  colId,
  operatorType = '',
}) => {
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const [isLowCardinality, setIsLowCardinality] = useState(false);

  const dispatch = useDispatch();
  const currentFile = useCurrentFile();

  const handleSelectValue = (newValue) => {
    setSelectedOptions(newValue);
    onChange(newValue);
  };

  const fetchOptions = async (query = '') => {
    const url = query
      ? `${AUTOCOMPLETE}/${currentFile.metadata.FileUuid}/${colId}/${query}`
      : `${AUTOCOMPLETE}/${
          currentFile.metadata.FileUuid
        }/${colId}?${QUERY_PARAM_OMIT_EMPTY_VALUES}=${isBlankOptionVisible(
          currFieldType,
          operatorType
        )}`;

    const response = await get(url);

    if (response.Success) {
      if (!response.Values) return;
      const fetchedOptions = response.Values.map((value) => ({
        value,
        label: value === '' ? '(Blanks)' : value,
      })).filter(
        (option, index, self) =>
          index === self.findIndex((o) => o.value === option.value && o.label === option.label)
      );

      setOptions(fetchedOptions);
      dispatch(setAutocompleteColumns({ colId, values: fetchedOptions }));
      setIsLowCardinality(response.isLowCardinality);
    } else {
      setOptions([]);
      setIsLowCardinality(true);
    }
  };

  useEffect(() => {
    if (defaultValue && Array.isArray(defaultValue)) {
      const selectedOpts = defaultValue.map((value) => {
        return {
          value,
          label: value === '' ? '(Blanks)' : value,
        };
      });

      setSelectedOptions(selectedOpts);
    }
  }, [defaultValue]);

  useEffect(() => {
    fetchOptions();
  }, []);

  const validateInput = (value) => {
    if (currFieldType?.includes('Int') && value.length) {
      const isValidNumber = Array.isArray(value)
        ? [...value].every((i) => Number.isInteger(Number(i)))
        : Number.isInteger(Number(value));
      if (!isValidNumber) return 'Input must be an integer.';
    } else if (currFieldType?.includes('Float') && value.length) {
      const isValidNumber = Array.isArray(value)
        ? [...value].every((i) => !isNaN(Number(i)))
        : !isNaN(Number(value));
      if (!isValidNumber) return 'Input must be a float.';
    }
    return null;
  };

  const splitValues = () => {
    const width = 50;
    const selectedValues = selectedOptions.map((i) => i?.label);
    const fullValues = [];
    let currentLength = 0;

    for (const str of selectedValues) {
      if (currentLength + str?.length <= width) {
        fullValues.push(str);
        currentLength += str?.length;
      } else {
        break;
      }
    }

    if (selectedValues.length > 0 && fullValues <= 0) {
      fullValues?.push(selectedValues[0]?.substring(0, 50) + '(...)');
    }

    const truncatedValueCount = selectedValues.length - fullValues.length;

    return { fullValues, truncatedValueCount };
  };

  return (
    <div className='set-filter__container' data-testid='SuggestionBasedFilter'>
      <ComboBox
        value={selectedOptions}
        options={options}
        onChange={(newValue) => {
          const originalValues = newValue.map((item) => item.value);
          handleSelectValue(originalValues);
        }}
        onInputChange={(value) => {
          fetchOptions(value);
        }}
        placeholder='Value1, Value2, ...'
        disabled={disabled}
        groupBy={null}
        selectFirstItem={false}
        freeSolo
        isOpenbyDefault={false}
        selectionMode='multiple'
        validateInput={validateInput}
        dataCy={cypressTextFieldName}
        dataTestId={cypressTextFieldName}
        renderSelectedValues={(selected) => {
          if (splitValues().truncatedValueCount > 0) {
            return (
              <>
                {splitValues().fullValues?.map((i = '') => {
                  return <span key={i}>{i}, </span>;
                })}
                <span key='selected-more'>+{splitValues()?.truncatedValueCount}</span>
              </>
            );
          } else {
            return selected.map((option, index) => (
              <span key={index}>
                {option.label}
                {index < selected.length - 1 ? ', ' : ''}
              </span>
            ));
          }
        }}
        showLimitedValueWarning={!isLowCardinality}
      />
    </div>
  );
};

export default SuggestionBasedFilter;
